import { List, Root as Tabs, Trigger } from '@radix-ui/react-tabs';
import Persist, { StorageKind } from 'lib/persist';
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';

import { AnchorLinkType } from 'components/anchorLink';
import { BookingTicket } from './types';
import { Button } from 'components/button';
import { IconsEnum } from 'components/Icons';
import { PRENOTAZIONE_SCOMMESSA_CODICESESSIONE } from 'features/carrello/constant';
import { PrenotazioniDetailContainer } from './prenotazioniDetailContainer';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { getCookie } from 'lib/persist/cookie';
import { selectTipo } from 'features/carrello/carrelloSelectors';
import { setAlertMessage } from 'features/dashboard/dashboardSlice';
import { setIsOpenLoginModalValue } from 'features/modal/modalSlice';
import styles from './PrenotazioniContent.module.scss';
import { useCancellaPrenotazioneMutation } from 'features/api/sportApiSlice';
import { useGetLabelByKey } from 'hooks/useLingUI';

const localStorage = Persist(StorageKind.local);

export type PrenotazioniContentProps = {
  refreshLocalStorage: Dispatch<SetStateAction<string[]>>;
};
export type PrenotazioniNavigation = {
  id: string;
  label: string;
  navigationItemBlock?: AnchorLinkType[];
};

export type PrenotazioniItemBlock = {
  id: string;
  label: string;
  linkBlock: AnchorLinkType[];
};
interface LocalStorageEntry {
  key: string;
  value: any;
}

export const PrenotazioniContent = ({ refreshLocalStorage }: PrenotazioniContentProps) => {
  const [tabValue, setTabValue] = useState<string>('prenotazioni');
  const refIdPrenotazione = useRef<string>('');
  const [scadenzaCodiceSessione, setScadenzaCodiceSessione] = useState<string>('');
  const [localStorageData, setLocalStorageData] = useState<string[]>([]);
  const [timeToDelay, setTimeToDelay] = useState<number[]>([]);
  const [codiceSessione, setCodiceSessione] = useState<string>('');
  const tipo = useTypedSelector(selectTipo);

  const keyStorage = `${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`;

  const dispatch = useAppDispatch();
  const [
    resultDeleteBooking,
    deleteBookingResponse,
    //{ status: isDeletedBooking, isLoading: isLoadingDeleteBooking },
  ] = useCancellaPrenotazioneMutation();

  const labelPrenotazioneCancellata = useGetLabelByKey('prenotazione-cancellata');
  const labelAccediPerScommesse = useGetLabelByKey('accedi-per-vedere-le-tue-scommesse');
  const labelPrenotazioni = useGetLabelByKey('prenotazioni');
  const labelLogin = useGetLabelByKey('login');

  const labelValiditaNickname = useGetLabelByKey('tempo-validita-nickname');

  useEffect(() => {
    const prenotazioniLocalStorageElements = [keyStorage];

    // Initialize an empty array to store the retrieved data
    const retrievedData: LocalStorageEntry[] = [];

    // Loop through the keys and read data from local storage
    prenotazioniLocalStorageElements.forEach((key) => {
      const item = localStorage.getItem(key);
      if (item) {
        retrievedData.push({ key, value: JSON.parse(item) });
      }
    });

    const prenotazioneCookie = getCookie(keyStorage);

    if (prenotazioneCookie) {
      const cookieValueParts = prenotazioneCookie && prenotazioneCookie.split('_');
      const codiceSessione = cookieValueParts[0];
      const scadenzaCodiceSessione = cookieValueParts[1];
      const currentDate = new Date();
      const expireDate = new Date(scadenzaCodiceSessione);
      const timeDifference = expireDate.getTime() - currentDate.getTime();
      const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
      const minutesDifference = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
      setScadenzaCodiceSessione(scadenzaCodiceSessione);
      setCodiceSessione(codiceSessione);
      setTimeToDelay([hoursDifference, minutesDifference]);
    }
    //console.log('retrievedData', JSON.parse(retrievedData[0].value));
    retrievedData && retrievedData.length > 0 && setLocalStorageData(retrievedData[0].value);
  }, [keyStorage]);

  useEffect(() => {
    if (deleteBookingResponse.status === QueryStatus.fulfilled) {
      // Add an event listener to detect changes in localStorage.
      const newLocalStorageData = localStorageData.filter((item) => {
        const prenotazione = JSON.parse(item);
        return prenotazione.idPrenotazione !== refIdPrenotazione.current;
      });
      setLocalStorageData(newLocalStorageData);
      if (localStorageData.length === 0) {
        localStorage.removeItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
      } else {
        localStorage.removeItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`);
        localStorage.setItem(`${PRENOTAZIONE_SCOMMESSA_CODICESESSIONE}-${tipo}`, JSON.stringify(newLocalStorageData));
      }
      dispatch(
        setAlertMessage({
          icon: IconsEnum.SUCCESS,
          message: `${labelPrenotazioneCancellata}`,
          color: 'var(--color-success)',
          animation: true,
        })
      );
      refreshLocalStorage(newLocalStorageData);
    } else if (deleteBookingResponse.status === QueryStatus.rejected) {
      dispatch(
        setAlertMessage({
          icon: IconsEnum.SUCCESS,
          message: deleteBookingResponse?.error['data']?.response?.desc_errore!,
          color: 'var(--color-warning)',
          animation: true,
        })
      );
    }
  }, [
    deleteBookingResponse?.error,
    deleteBookingResponse.status,
    dispatch,
    labelPrenotazioneCancellata,
    localStorageData,
    refreshLocalStorage,
    tipo,
  ]);

  const removePrenotazione = async (codiceSessione: string, idPrenotazione: string): Promise<void> => {
    refIdPrenotazione.current = idPrenotazione;
    await resultDeleteBooking({
      codiceSessione,
      idPrenotazione,
    }).catch((e) => {
      console.log(e);
    });
  };
  console.log('timeToDelay', timeToDelay);
  return (
    <div className={`${styles.scrollList}`}>
      {localStorageData && localStorageData.length > 0 ? (
        <React.Fragment>
          <div className={styles.subheader}>
            <Tabs defaultValue={'prenotazioni'} value={tabValue}>
              <List className={styles.tablist}>
                <Trigger value={'prenotazioni'} className={styles.tab} onClick={() => setTabValue('prenotazioni')}>
                  {labelPrenotazioni}
                  <span className={styles.tabnumber}>({localStorageData.length})</span>
                </Trigger>
              </List>
            </Tabs>
          </div>
          <div className={styles.content}>
            <p className={styles.delay}>
              {timeToDelay?.length > 0 &&
                labelValiditaNickname!
                  .replace('{{valoreOre}}', `${timeToDelay[0]}`)
                  .replace('{{valoreMinuti}}', `${timeToDelay[1]}`)}
            </p>
            <p className={styles.code}>{codiceSessione}</p>
          </div>
        </React.Fragment>
      ) : (
        <div className={styles.emptyWrapper}>
          <div>
            <p className={styles.text}>{labelAccediPerScommesse}</p>
            <Button
              size="medium"
              className={styles.btnLogin}
              visualizationType="primary"
              suppressHydrationWarning={true}
              onClick={() => dispatch(setIsOpenLoginModalValue(true))}
            >
              {labelLogin}
            </Button>
          </div>
        </div>
      )}
      {localStorageData.map((entry) => {
        const ticket: BookingTicket = JSON.parse(entry);
        return (
          <PrenotazioniDetailContainer
            codiceSessione={codiceSessione}
            key={ticket?.idPrenotazione}
            removePrenotazione={removePrenotazione}
            bookingTicket={ticket}
            scadenzaCodiceSessione={scadenzaCodiceSessione}
          />
        );
      })}
    </div>
  );
};
